// RegistrationModals.tsx
import { MouseEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { TableData } from "../../../core/data/interface";
import { deleteRegistration } from "../../../service/registration";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import { selectBranches, selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName } from "../../../utils/campusLogos";

interface RegistrationData extends TableData {
  registrationNumber: string;
  firstName: string;
  gender: string;
  status: string;
  createdAt: string;
  dateOfBirth: string;
  fatherEmail: string;
  fatherPhoneNumber: string;
  _id: string;
  lastName: string;
  studentPicture: string;
  academicYear: string;
  fatherName: string;
  testGroup?: any;
  interviewDate: string;
  interviewTime: string;
  // The registration's OWN campus — preferred over whatever's globally
  // selected in the header when present.
  branch?: string;
}

type Props = {
  selectedId?: string | null;
  setSelectedId?: (id: string | null) => void;
  fetchData?: () => void;
  registrationData?: RegistrationData | null;
  testGroupData?: {
    testDateTime?: string;
  } | null;
  isFromDetails?: boolean;
};

const RegistrationModals = ({
  selectedId,
  setSelectedId,
  fetchData,
  registrationData,
  isFromDetails = false,
}: Props) => {
  const branches = useSelector(selectBranches);
  const currentBranch = useSelector(selectCurrentBranch);
  const recordBranch =
    branches.find((b) => b._id === registrationData?.branch) || currentBranch;
  const [loading, setLoading] = useState(false);
  const printContentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printContentRef });

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!selectedId || loading) {
      return;
    }

    setLoading(true);
    try {
      await deleteRegistration(selectedId);
      setSelectedId?.(null);
      toast.success("Registration deleted successfully!");

      const closeButton = document.querySelector(
        '[data-bs-dismiss="modal"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      }

      fetchData?.();
    } catch (error) {
      toast.error("Failed to delete registration form!");
    } finally {
      setLoading(false);
    }
  };

  const getInterviewDetails = () => {
    if (!registrationData) return { date: "", time: "", type: "" };

    if (registrationData?.testGroup?.testDateTime) {
      return {
        date: dayjs(registrationData.testGroup.testDateTime).format(
          "DD-MM-YYYY"
        ),
        time: dayjs(registrationData.testGroup.testDateTime).format("hh:mm A"),
        type: "Written Test/Interview",
      };
    } else if (registrationData?.interviewDate) {
      return {
        date: dayjs(registrationData.interviewDate).format("DD-MM-YYYY"),
        time: registrationData?.interviewTime
          ? dayjs(`2000-01-01T${registrationData.interviewTime}`).format(
              "hh:mm A"
            )
          : "",
        type: "Interview",
      };
    }
    return { date: "", time: "", type: "Test/Interview" };
  };

  const interviewDetails = getInterviewDetails();

  return (
    <>
      {/* Schedule Test/Interview Modal - Only show if registrationData exists */}
      <div className="modal fade" id="add_fees_collect">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center">
                <h4 className="modal-title">Admit Card</h4>
              </div>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            {/* This div will be printed */}
            <div className="modal-body">
              <div
                id="printable-content"
                ref={printContentRef}
                className="printable-content"
              >
                <div className="d-flex justify-content-center align-items-center mb-2">
                  <div className="logo-small img">
                    {recordBranch?.blackLogoUrl ? (
                      <img src={recordBranch.blackLogoUrl} alt="Logo" />
                    ) : (
                      <ImageWithBasePath
                        src="assets/img/icons/logo.png"
                        alt="Logo"
                      />
                    )}
                  </div>
                  <div className="d-flex flex-column">
                    <h1 className="fw-bold fs-1">{campusBrandName(recordBranch).toUpperCase()}</h1>
                    <p className="text-center">
                      {recordBranch?.address || ""}
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <h3 className="fw-bold mb-0 border border-light p-3 text-center col-6">
                    Admit Card for {interviewDetails.type}
                  </h3>
                  <div className=" border border-light p-1 col-6">
                    <div className="fw-bold border-bottom">
                      Registration# {registrationData?.registrationNumber}
                    </div>
                    <span className="fw-bold">Form# R.SN#</span>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <p>
                    Received Registration Form for admission to Class/Grade:
                  </p>
                  <div className="border-bottom mb-3">
                    {registrationData?.appliedClass?.name || "___________"}
                  </div>
                </div>
                <table className="table table-bordered mb-3">
                  <tbody>
                    <tr>
                      <td className="fw-bold">1. Name of Student</td>
                      <td>{`${registrationData?.firstName || ""} ${
                        registrationData?.lastName || ""
                      }`}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">2. Father's Name</td>
                      <td>{registrationData?.fatherName || ""}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="mb-4 d-flex gap-2 flex-wrap align-items-center">
                  <p className="mb-0">
                    Parents are requested to come along with the child for{" "}
                    {interviewDetails.type} on
                  </p>
                  {interviewDetails.date && (
                    <div className="d-flex justify-content-center gap-3 mb-3">
                      <div className="border-bottom text-center px-3">
                        {interviewDetails.date}
                      </div>
                      {interviewDetails.time && (
                        <>
                          <span>at</span>
                          <div className="border-bottom text-center px-3">
                            {interviewDetails.time}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="text-end mt-5">
                  <div className="border-top pt-2 d-inline-block">
                    <p className="mb-0">Signature of Admin</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handlePrint}
              >
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash-x" />
              </span>
              <h4>Confirm Deletion</h4>
              <p>
                You want to delete all the marked items, this cant be undone
                once you delete.
              </p>

              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-light me-3"
                  data-bs-dismiss="modal"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={loading}
                  data-bs-dismiss="modal"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Deleting...
                    </>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegistrationModals;
